/* eslint-disable no-unsafe-optional-chaining */
import { CachedItem } from "@lib-shared/lib";
import { sessionManager } from "core/instances";
import { logApiError } from "core/lib";
import { OtpService, PermissionService } from "core/services/cef";
import { OtpDeliveryRequest } from "core/services/cef/otp.service";
import { RegisterUserRequest } from "core/services/cef/permission.service.types";
import { Agreement } from "core/services/cef/types";
import { ModalAlert } from "components/common-ui";
import LinkUpStore, { SendOtpTo } from "./link-up.store";
import { getAccounts, getAndLogError, showErrorToUser } from "./link-up-customer.helpers";
import { BusinessAccount } from "./types";
import getUserDetailsAsync, { userStatus } from "core/lib/get-user-details";
import { noop } from "lodash";

const permissionService = new PermissionService();

export const LinkUpCustomerMode = {
    noAccess: -1,
    notFound: -2,
    search: 0,
    select: 1,
    confirm: 2,
    enterOtp: 3,
    getUser: 4
};

type onLinkupFunction = () => Promise<void>;

class LinkUpCustomerLogic {
    private gotoDashboard = noop;
    private onLinkUp = noop;

    constructor(
        private _store = new LinkUpStore(),
        private useCache: boolean = false) {
    }

    get store() {
        return this._store;
    }

    setEvents(onLinkup: onLinkupFunction, gotoDashboard: () => void) {
        this.onLinkUp = onLinkup;
        this.gotoDashboard = gotoDashboard;
    }

    async checkToken() {
        const hasAccess = await sessionManager.hasAccessAsync(false);

        if (hasAccess) {
            return;
        }

        this._store.updateDisplayMode(LinkUpCustomerMode.noAccess);
    }

    async checkUser() {
        const getUserDetails = async () => {
            console.log("Getting user details");

            try {
                const { status } = await getUserDetailsAsync();

                if (status !== userStatus.active) {
                    return;
                }

                this.gotoDashboard();
            } catch (error) {
                logApiError(error);
                return;
            }
        }

        const user = sessionManager.loggedInUser;

        if (user == null) {
            getUserDetails();
            return;
        }

        this.gotoDashboard();
    }

    async search(searchType: string, searchValue: string) {
        const store = this._store;
        const useCache = this.useCache;

        const testCache = useCache ? new CachedItem(`test.customer.${searchType}.${searchValue}`) : null;

        try {
            const isCached = testCache && useCache ? testCache.isCached : false;

            const result = testCache && isCached
                ? testCache.get()
                : await permissionService.customerSearchAsync(searchType, searchValue);

            store.updateResult(result.data);

            const status = result?.status;

            if (status !== 200) {
                console.error(result);
                throw result.data;
            }

            if (testCache && useCache && !isCached) {
                testCache.set(result);
            }

            try {
                const simplifiedData = getAccounts(result.data);

                store.updateAccounts(simplifiedData);

                store.updateDisplayMode(LinkUpCustomerMode.select);
            } catch (error) {
                console.error(error);

                ModalAlert.error("Error getting accounts");
            }
        } catch (error: any) {
            logApiError(error);

            const status = error?.response?.status;

            if ([400, 404].includes(status)) {
                store.updateDisplayMode(LinkUpCustomerMode.notFound);
                return;
            }

            const message = getAndLogError(error, "Error searching")

            ModalAlert.error(message);

            store.updateIsSearching(false);
        }
    }

    selectAccounts(userSelectedAccounts: BusinessAccount[]) {
        const store = this._store;

        const firstAccount = userSelectedAccounts[0];

        if (!firstAccount) {
            return;
        }

        const encrypted = firstAccount.encrypted;

        const contacts = [
            {
                key: `sms-${store.otpKey}`,
                mode: "sms",
                value: firstAccount.contactNumber,
                encryptedValue: encrypted.cell
            },
            {
                key: `email-${store.otpKey}`,
                mode: "email",
                value: firstAccount.contactEmail,
                encryptedValue: encrypted.email
            }
        ];

        store.updateDeliveryOptions(contacts.filter(x => x.value != null && x.value.length > 0));

        store.updateDisplayMode(LinkUpCustomerMode.confirm);
    }

    async tryToSendOtpAsync(contact: SendOtpTo | undefined) {
        if (!contact) {
            return;
        }

        const request: OtpDeliveryRequest = {
            key: contact.key,
            encryptedValue: contact.encryptedValue,
            mode: contact.mode,
        };

        try {
            const response = await new OtpService().sendAsync(request);
            console.dir(response?.data);
            return true;
        } catch (error) {
            logApiError(error);
            showErrorToUser(error, "Error sending otp");
            return false;
        }
    }

    async sendOtpAsync(contact: any) {
        this._store.updateSendOtpTo(contact);

        const isSent = await this.tryToSendOtpAsync(contact);

        if (isSent) {
            this._store.updateDisplayMode(LinkUpCustomerMode.enterOtp);
        }
    }

    async validateOtpAndLinkUpAsync(otp: string) {
        const store = this._store;

        const result = store.result;

        if (!result) {
            ModalAlert.error("Please refresh the page & try again");
            return;
        }

        const { firstName, lastName } = sessionManager.getNameOfUser();

        const { companyName, encryptedCustomerNumber } = result.customer;

        const agreements = result.agreements.map(x => {
            return {
                id: x.id,
                friendlyName: x.friendlyName
            } as Agreement
        });

        const request: RegisterUserRequest = {
            firstName: firstName,
            lastName: lastName,
            //phone: //shouldn't be needed
            encryptedCustomerNumber: encryptedCustomerNumber,
            agreements: agreements,
            companyName: companyName,
            role: "Primary",
            oneTimePin: {
                encryptedValue: store.sendOtpTo?.encryptedValue ?? "",
                key: store.sendOtpTo?.key ?? "",
                otp: otp ?? ""
            }
        };

        const defaultErrorMessage = "The OTP is invalid or has expired";

        try {
            const response = await permissionService.registerPrimaryUser(request);

            console.log(response);

            const status = response.data?.status;

            if (status && status?.includes("Linked user")) {
                await this.onLinkUp();
                return;
            }

            const message = getAndLogError({ response }, defaultErrorMessage);
            store.updateOtpErrorMessage(message);
        } catch (error) {
            logApiError(error);

            const message = getAndLogError(error, "Error validating otp");
            store.updateOtpErrorMessage(message);
        }
    }

    async resendOtpAsync() {
        const store = this._store;

        const hasSent = await this.tryToSendOtpAsync(store.sendOtpTo);

        if (!hasSent) {
            return;
        }

        store.updateShowResentMessage(true);

        setTimeout(() => {
            store.updateShowResentMessage(false);
        }, 30000); //30 secs
    }
}

/* 
    Note: In the link up customer component, it's not possible to just pass say logic.search 
    as this will be undefined. Hence the need for this.
*/
export const getLogicFunctions = (logic: LinkUpCustomerLogic) => {
    const search = (searchType: string, searchValue: string) => logic.search(searchType, searchValue);

    const selectAccounts = (userSelectedAccounts: BusinessAccount[]) => logic.selectAccounts(userSelectedAccounts);

    const resendOtpAsync = () => logic.resendOtpAsync();

    const sendOtpAsync = (contact: any) => logic.sendOtpAsync(contact);

    const validateOtpAndLinkUpAsync = (otp: string) => logic.validateOtpAndLinkUpAsync(otp);

    return [search, selectAccounts, resendOtpAsync, sendOtpAsync, validateOtpAndLinkUpAsync];
}

export default LinkUpCustomerLogic;